import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft, GripVertical, Copy } from 'lucide-react';
import { useData } from '../../context/DataContext';
import { Product } from '../../types';

const AdminProducts: React.FC = () => {
  const { products, loading, error, updateProduct, loadAllProducts } = useData();
  const navigate = useNavigate();
  const [sortedProducts, setSortedProducts] = useState<Product[]>([]);
  const [draggedItem, setDraggedItem] = useState<Product | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState<boolean>(false);

  // Load all products when admin page mounts
  useEffect(() => {
    loadAllProducts();
  }, []);

  useEffect(() => {
    // Filter products based on showInactive
    const filtered = showInactive ? products : products.filter(p => p.active !== false);
    
    // Sort products by displayOrder, then by name for consistency
    const sorted = [...filtered].sort((a, b) => {
      if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
        return a.displayOrder - b.displayOrder;
      }
      if (a.displayOrder !== undefined) return -1;
      if (b.displayOrder !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
    setSortedProducts(sorted);
  }, [products, showInactive]);

  const handleProductClick = (productId: string) => {
    navigate(`/admin/products/${productId}`);
  };

  const handleAddProduct = () => {
    navigate('/admin/products/new');
  };

  const handleCopyProduct = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    navigate(`/admin/products/new?copy=${product.id}`);
  };

  const handleDragStart = (e: React.DragEvent, product: Product) => {
    setDraggedItem(product);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedItem) return;

    const draggedIndex = sortedProducts.findIndex(p => p.id === draggedItem.id);
    if (draggedIndex === dropIndex) return;

    // Create new array with reordered items
    const newProducts = [...sortedProducts];
    newProducts.splice(draggedIndex, 1);
    newProducts.splice(dropIndex, 0, draggedItem);

    // Update displayOrder for all affected products
    const updatedProducts = newProducts.map((product, index) => ({
      ...product,
      displayOrder: index
    }));

    setSortedProducts(updatedProducts);

    // Update each product with new displayOrder
    try {
      for (const product of updatedProducts) {
        if (product.displayOrder !== products.find(p => p.id === product.id)?.displayOrder) {
          await updateProduct(product);
        }
      }
    } catch (err) {
      console.error('Failed to update product order:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-blue-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin
        </Link>
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold">Product Management</h1>
          <button
            onClick={handleAddProduct}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Product
          </button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded">
          <p className="text-sm">Drag and drop products to reorder them. The order here will be reflected on the public products page.</p>
        </div>
        <div className="mt-4 flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show inactive products</span>
          </label>
          {!showInactive && products.filter(p => p.active === false).length > 0 && (
            <span className="ml-4 text-sm text-gray-500">
              ({products.filter(p => p.active === false).length} hidden)
            </span>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Product
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Base Price
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Size Limits
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProducts.map((product, index) => (
              <tr 
                key={product.id} 
                className={`hover:bg-gray-50 cursor-pointer transition-all ${
                  dragOverIndex === index ? 'bg-blue-50 border-t-2 border-blue-400' : ''
                } ${product.active === false ? 'opacity-60' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, product)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center text-gray-400 hover:text-gray-600">
                    <GripVertical className="h-5 w-5" />
                  </div>
                </td>
                <td className="px-6 py-4" onClick={() => handleProductClick(product.id)}>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-400 text-xs">No image</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="text-lg font-medium text-gray-900">{product.name}</div>
                      <div className="text-sm text-gray-600">{product.category}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4" onClick={() => handleProductClick(product.id)}>
                  <div className="text-lg font-semibold text-gray-900">
                    {!product.customizable && product.fixedSizes && product.fixedSizes.length > 0 ? (
                      <>
                        {(() => {
                          const prices = product.fixedSizes.map(size => size.price);
                          const minPrice = Math.min(...prices);
                          const maxPrice = Math.max(...prices);
                          if (minPrice === maxPrice) {
                            return `$${minPrice.toFixed(2)}`;
                          }
                          return `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`;
                        })()}
                      </>
                    ) : (
                      <>
                        ${product.basePrice.toFixed(2)}<span className="text-sm text-gray-500 font-normal">/sq ft</span>
                      </>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4" onClick={() => handleProductClick(product.id)}>
                  {product.customizable ? (
                    <div className="text-sm text-gray-700">
                      <div>Min: {product.minWidth || 'N/A'}" × {product.minHeight || 'N/A'}"</div>
                      <div>Max: {product.maxWidth || 'N/A'}" × {product.maxHeight || 'N/A'}"</div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">—</div>
                  )}
                </td>
                <td className="px-6 py-4" onClick={() => handleProductClick(product.id)}>
                  <div className="text-sm">
                    {product.active !== false ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        Inactive
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <button
                    onClick={(e) => handleCopyProduct(product, e)}
                    className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                    title="Copy product"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminProducts;